import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaBook, FaBookOpen, FaSearch, FaHistory } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { organizationApi, employeesApi, projectsApi } from '../../services/api';
import { useAlert } from '../../contexts/AlertContext';
import LoadingSpinner from '../common/LoadingSpinner';
import GradeHistoryModal from './GradeHistoryModal';
import PositionHistoryModal from './PositionHistoryModal';
import ProjectHistoryModal from './ProjectHistoryModal';
import RoleHistoryModal from './RoleHistoryModal';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ContentWrapper,
  ConfigurationSection,
  SectionTitle,
  OptionsContainer,
  OptionButton,
  LegendSection,
  LegendTitle,
  LegendGrid,
  LegendItem,
  HeaderButton,
  TabContainer,
  TabButton,
  ProjectsContainer,
  ProjectBadge,
  SearchInput,
  SearchContainer,
  SearchIcon,
} from './styles';

import { ROLE_TYPES } from '../../constants/roleTypes';

const TABS = {
  ROLES: 'roles',
  PROJECTS: 'projects'
};

const EmployeeOrgConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const employeeUser = location.state?.user;
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedFunctionalRoles, setSelectedFunctionalRoles] = useState([]);
  const [selectedOrgRoles, setSelectedOrgRoles] = useState([]);
  const [selectedTechRoles, setSelectedTechRoles] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [legendData, setLegendData] = useState({ gradeLegend: [], roleLegend: [] });
  const [structureData, setStructureData] = useState({
    grades: [],
    positions: [],
    roles: []
  });
  const [activeTab, setActiveTab] = useState(TABS.ROLES);
  const [projects, setProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [showGradeHistory, setShowGradeHistory] = useState(false);
  const [gradeHistory, setGradeHistory] = useState([]);
  const [showPositionHistory, setShowPositionHistory] = useState(false);
  const [positionHistory, setPositionHistory] = useState([]);
  const [showProjectHistory, setShowProjectHistory] = useState(false);
  const [projectHistory, setProjectHistory] = useState([]);
  const [showRoleHistory, setShowRoleHistory] = useState(false);
  const [roleHistory, setRoleHistory] = useState([]);

  useEffect(() => {
    if (activeTab === TABS.PROJECTS) {
      setShowLegend(false);
    }
  }, [activeTab]);

  useEffect(() => {
    if (!employeeUser) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        const [
          legendResponse, 
          structureResponse, 
          organizationResponse, 
          projectsResponse, 
          employeeResponse, 
          gradeHistoryResponse,
          positionHistoryResponse,
          projectHistoryResponse,
          roleHistoryResponse
        ] = await Promise.all([
          organizationApi.getLegend(),
          organizationApi.getStructure(),
          employeesApi.getEmployeeOrganization(employeeUser.id),
          projectsApi.getAllProjects(),
          employeesApi.getEmployeeById(employeeUser.id),
          employeesApi.getEmployeeGradeHistory(employeeUser.id),
          employeesApi.getEmployeePositionHistory(employeeUser.id),
          employeesApi.getEmployeeProjectHistory(employeeUser.id),
          employeesApi.getEmployeeRoleHistory(employeeUser.id)
        ]);
        
        setLegendData(legendResponse);
        setStructureData(structureResponse);
        setProjects(projectsResponse);
        setGradeHistory(gradeHistoryResponse);
        setPositionHistory(positionHistoryResponse);
        setProjectHistory(projectHistoryResponse);
        setRoleHistory(roleHistoryResponse);
        
        // Set initial selections from organization data
        setSelectedGrade(organizationResponse.grade?.label || null);
        setSelectedPosition(organizationResponse.position?.label || null);
        
        // Group roles by type and set selections
        const roles = organizationResponse.roles || [];
        setSelectedFunctionalRoles(roles.filter(r => r.type === ROLE_TYPES.FUNCTIONAL).map(r => r.label));
        setSelectedOrgRoles(roles.filter(r => r.type === ROLE_TYPES.ORGANIZATIONAL).map(r => r.label));
        setSelectedTechRoles(roles.filter(r => r.type === ROLE_TYPES.TECHNICAL).map(r => r.label));

        // Set selected projects from employee data
        if (employeeResponse.projects) {
          setSelectedProjects(employeeResponse.projects.map(p => p.id));
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        showAlert('Failed to load configuration data', 'error', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [employeeUser, navigate, showAlert]);


  const handleBack = () => {
    navigate(-1);
  };

  const filteredProjects = projects.filter(project => 
    project.name.toLowerCase().includes(projectSearchTerm.toLowerCase())
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  const sortedGrades = [...structureData.grades].sort((a, b) => a.order - b.order);
  const sortedPositions = [...structureData.positions].sort((a, b) => a.order - b.order);

  const getFunctionalRoles = () => {
    if (!selectedPosition) return [];
    const position = structureData.positions.find(p => p.label === selectedPosition);
    if (!position) return [];
    return position.roles
      .filter(role => role.type === ROLE_TYPES.FUNCTIONAL)
      .sort((a, b) => a.order - b.order);
  };

  const getOrganizationalRoles = () => {
    if (!selectedPosition) return [];
    const position = structureData.positions.find(p => p.label === selectedPosition);
    if (!position) return [];
    return position.roles
      .filter(role => role.type === ROLE_TYPES.ORGANIZATIONAL)
      .sort((a, b) => a.order - b.order);
  };

  const getTechnicalRoles = () => {
    if (!selectedPosition) return [];
    const position = structureData.positions.find(p => p.label === selectedPosition);
    if (!position) return [];
    return position.roles
      .filter(role => role.type === ROLE_TYPES.TECHNICAL)
      .sort((a, b) => a.order - b.order);
  };

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Skills Configuration
          </BackButton>
          <Title>Employee Configuration | {employeeUser.name}</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '8px' }}>
          {activeTab === TABS.ROLES && (
            <HeaderButton onClick={() => setShowLegend(!showLegend)}>
              {showLegend ? <FaBookOpen /> : <FaBook />}
              {showLegend ? 'Hide Legend' : 'Show Legend'}
            </HeaderButton>
          )}
        </div>
      </Header>

      <TabContainer>
        <TabButton
          active={activeTab === TABS.ROLES}
          onClick={() => setActiveTab(TABS.ROLES)}
        >
          Roles
        </TabButton>
        <TabButton
          active={activeTab === TABS.PROJECTS}
          onClick={() => setActiveTab(TABS.PROJECTS)}
        >
          Projects
        </TabButton>
      </TabContainer>

      <Content>
        {activeTab === TABS.ROLES ? (
          <ContentWrapper>
            <ConfigurationSection>
              <SectionTitle type="grade">
                grade
                <FaHistory 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px'
                  }} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowGradeHistory(true);
                  }}
                />
              </SectionTitle>
              <OptionsContainer>
                {sortedGrades.map((grade) => (
                  <OptionButton
                    key={grade.id}
                    type="grade"
                    selected={selectedGrade === grade.label}
                    disabled={false}
                  >
                    {grade.label}
                  </OptionButton>
                ))}
              </OptionsContainer>
            </ConfigurationSection>

            <ConfigurationSection>
              <SectionTitle type="position">
                position
                <FaHistory 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px'
                  }} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPositionHistory(true);
                  }}
                />
              </SectionTitle>
              <OptionsContainer>
                {sortedPositions.map((position) => (
                  <OptionButton
                    key={position.id}
                    type="position"
                    selected={selectedPosition === position.label}
                    disabled={false}
                  >
                    {position.label}
                  </OptionButton>
                ))}
              </OptionsContainer>
            </ConfigurationSection>

            <ConfigurationSection>
              <SectionTitle type={ROLE_TYPES.FUNCTIONAL}>
                functional role
                <FaHistory 
                  style={{ 
                    marginLeft: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px'
                  }} 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRoleHistory(true);
                  }}
                />
              </SectionTitle>
              <OptionsContainer>
                {getFunctionalRoles().map((role) => (
                  <OptionButton
                    key={role.id}
                    type={ROLE_TYPES.FUNCTIONAL}
                    selected={selectedFunctionalRoles.includes(role.label)}
                  >
                    {role.label}
                  </OptionButton>
                ))}
              </OptionsContainer>
            </ConfigurationSection>

            <ConfigurationSection>
              <SectionTitle type={ROLE_TYPES.ORGANIZATIONAL}>organizational role</SectionTitle>
              <OptionsContainer>
                {getOrganizationalRoles().map((role) => (
                  <OptionButton
                    key={role.id}
                    type={ROLE_TYPES.ORGANIZATIONAL}
                    selected={selectedOrgRoles.includes(role.label)}
                  >
                    {role.label}
                  </OptionButton>
                ))}
              </OptionsContainer>
            </ConfigurationSection>

            <ConfigurationSection>
              <SectionTitle type={ROLE_TYPES.TECHNICAL}>technical role</SectionTitle>
              <OptionsContainer>
                {getTechnicalRoles().map((role) => (
                  <OptionButton
                    key={role.id}
                    type={ROLE_TYPES.TECHNICAL}
                    selected={selectedTechRoles.includes(role.label)}
                  >
                    {role.label}
                  </OptionButton>
                ))}
              </OptionsContainer>
            </ConfigurationSection>
          </ContentWrapper>
        ) : (
          <ContentWrapper>
            <div style={{ padding: '24px 24px 0 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <SearchContainer>
                  <SearchInput
                    type="text"
                    placeholder="Search projects..."
                    value={projectSearchTerm}
                    onChange={(e) => setProjectSearchTerm(e.target.value)}
                  />
                  <SearchIcon>
                    <FaSearch size={14} />
                  </SearchIcon>
                </SearchContainer>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  height: '40px',
                  padding: '0 12px',
                  marginTop: '-24px'
                }}>
                  <FaHistory 
                    style={{ 
                      cursor: 'pointer',
                      fontSize: '18px',
                      color: '#666'
                    }} 
                    onClick={() => setShowProjectHistory(true)}
                  />
                </div>
              </div>
              <ProjectsContainer>
                {filteredProjects.map((project) => (
                  <ProjectBadge
                    key={project.id}
                    selected={selectedProjects.includes(project.id)}
                  >
                    {project.name}
                  </ProjectBadge>
                ))}
              </ProjectsContainer>
            </div>
          </ContentWrapper>
        )}

        {showLegend && (
          <>
            <LegendSection>
              <LegendTitle type="grade">grade</LegendTitle>
              <LegendGrid>
                {legendData.gradeLegend.map(({ grade, description }) => (
                  <LegendItem key={grade} type="grade">
                    <div>{grade}</div>
                    <div>{description}</div>
                  </LegendItem>
                ))}
              </LegendGrid>
            </LegendSection>

            <LegendSection>
              <LegendTitle type="role">role</LegendTitle>
              <LegendGrid>
                {legendData.roleLegend.map(({ role, description }) => (
                  <LegendItem key={role} type="role">
                    <div>{role}</div>
                    <div>{description}</div>
                  </LegendItem>
                ))}
              </LegendGrid>
            </LegendSection>
          </>
        )}
      </Content>
      <GradeHistoryModal
        isOpen={showGradeHistory}
        onClose={() => setShowGradeHistory(false)}
        gradeHistory={gradeHistory}
        employeeName={employeeUser?.name}
        onSubmit={() => {}}
      />
      <PositionHistoryModal
        isOpen={showPositionHistory}
        onClose={() => setShowPositionHistory(false)}
        positionHistory={positionHistory}
        employeeName={employeeUser?.name}
        onSubmit={() => {}}
      />
      <ProjectHistoryModal
        isOpen={showProjectHistory}
        onClose={() => setShowProjectHistory(false)}
        projectHistory={projectHistory}
        employeeName={employeeUser?.name}
        onSubmit={() => {}}
      />
      <RoleHistoryModal
        isOpen={showRoleHistory}
        onClose={() => setShowRoleHistory(false)}
        roleHistory={roleHistory}
        employeeName={employeeUser?.name}
        onSubmit={() => {}}
      />
    </Container>
  );
};

export default EmployeeOrgConfiguration; 